
import Component from 'vue-class-component';
import Vue from 'vue';
import { namespace } from 'vuex-class';
import { Loan } from '@/loan/types/loan';
import { LoanDetails as LoanDetailsInterface } from '@/loan/types/loan-details';
import LoanAutopayDisableModal from '@/loan/components/LoanAutopayDisableModal.vue';
import { ModalState } from '@/core/types/modal-state';
import { LoanAPIService } from '@/loan/services/api';
import IconWithTooltip from '@/core/components/IconWithTooltip.vue';
import { LoanLinkedAccount } from '@/loan/types/loan-linked-account';
import { LoanFrequency } from '@/loan/enums/loan-frequency';
import { LoanHelper } from '@/loan/helper';
import { UtilHelper } from '@/util/helper';
import TooltipWithTextInput from '@/core/components/TooltipWithTextInput.vue';
import { LoanUpdateLoanRequest } from '@/loan/types/loan-update-loan-request';

const TEST_ID = 'Kloans-Loan-LoanDetails';

const loanNamespace = namespace('loan');

const dollarFormatterExtended = (amount: number | string): string => {
	const value = amount ?? 0;

	return UtilHelper.formatDollar(value);
};

@Component({
	components: {
		LoanAutopayDisableModal,
		IconWithTooltip,
		TooltipWithTextInput,
	},
})
export default class LoanDetails extends Vue {
	public TEST_ID = TEST_ID;

	@loanNamespace.Getter('amountAvailableToTakeBack')
	public amountAvailableToTakeBack!: boolean;

	@loanNamespace.Getter('amountPastDue')
	public amountPastDue!: number;

	@loanNamespace.Getter('autopayFrequency')
	public autopayFrequency!: LoanFrequency;

	@loanNamespace.Getter('autopayLinkedAccount')
	public autopayLinkedAccount!: LoanLinkedAccount;

	@loanNamespace.Getter('autopayMinimum')
	public autopayMinimum!: number;

	@loanNamespace.Getter('autopayPaymentAmount')
	public autopayPaymentAmount!: number;

	public disableAutopayModalState: ModalState = { isShown: false, isLoading: false };

	public formatDate = UtilHelper.formatISOStringForDisplay;

	public formatDollar = dollarFormatterExtended;

	public formatPhone = UtilHelper.formatPhoneNumber;

	public isLoading: boolean = false;

	public isShown: boolean = false;

	public descriptionSync = '';

	@loanNamespace.Action('getAndSetLoanDetails')
	public getAndSetLoanDetails!: (loanId: string) => void;

	@loanNamespace.Getter('interestRate')
	public interestRate!: number;

	@loanNamespace.Getter('isAutopayOn')
	public isAutopayOn!: boolean;

	@loanNamespace.State('loan')
	public loan!: Loan;

	@loanNamespace.State('loanDetails')
	public loanDetails!: LoanDetailsInterface;

	@loanNamespace.Getter('loanId')
	public loanId!: string;

	@loanNamespace.Getter('minimumMonthlyPayment')
	public minimumMonthlyPayment!: number;

	public ordinal = UtilHelper.ordinal;

	@loanNamespace.Getter('outstandingFees')
	public outstandingFees!: number;

	@loanNamespace.Getter('payoffAmount')
	public payoffAmount!: number;

	public percent = UtilHelper.convertNumberToPercentString;

	get autopayFrequencyText(): string {
		return LoanHelper.getFrequencyText(this.autopayFrequency);
	}

	get loanNumberLastFour(): string {
		return this.loanDetails.general.kasasaLoanNumber.substr(-4);
	}

	public onDisableAutopay(): void {
		this.disableAutopayModalState = { isShown: true, isLoading: false };
	}

	public async disableAutopay(): Promise<void> {
		this.disableAutopayModalState = { isShown: true, isLoading: true };

		try {
			await LoanAPIService.disableAutopay(this.loanId);
			await this.getAndSetLoanDetails(this.loanId);

			this.disableAutopayModalState = { isShown: false, isLoading: false };
		} catch (error) {
			console.error(error);
			this.disableAutopayModalState = { isShown: true, isLoading: false };
		}
	}

	public tooltipClosed(): void {
		this.isShown = false;
	}

	public async onDescriptionChange(description: string): Promise<void> {
		this.isLoading = true;

		const request: LoanUpdateLoanRequest = {
			description,
		};

		try {
			await LoanAPIService.updateLoan(this.loanId, request);
			await this.getAndSetLoanDetails(this.loanId);
		} catch (error) {
			console.error(error);
		} finally {
			this.isLoading = false;
			this.isShown = false;
		}
	}
}
